import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card, CardHeader, CardContent } from "./card";
import { Button } from "./button";
import { Input } from "./input";
import { useToast } from "./use-toast";
import { Loader2, Eye, EyeOff, ChevronRight } from "lucide-react";
import { useGoogleLogin } from '@react-oauth/google';
import { GoogleLogin } from '@react-oauth/google';
import { trackAuthEvents } from '../utils/ga4Events';

import googlesignin from './img/android_light_rd_SI@4x.png';
import googlesignup from './img/android_light_rd_SU@4x.png';

const API_URL = process.env.REACT_APP_API_URL ? `${process.env.REACT_APP_API_URL}/api` : '/api';

const AuthComponent = ({ onLogin }) => {
  const [mode, setMode] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showVerificationButton, setShowVerificationButton] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const specialChar = /[!@#§/=$%^&*(),.?":{}|<>_~;°-]/;

  const renderGoogleButton = () => (
    <>
      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <span className="w-full border-t" />
        </div>
        <div className="relative flex justify-center text-xs uppercase">
          <span className="bg-gray-50 px-2 text-muted-foreground">
            Or continue with
          </span>
        </div>
      </div>
      <div className="w-full flex justify-center transition-opacity duration-200 ease-in-out hover:opacity-70">        
        <GoogleLogin
          onSuccess={handleGoogleSuccess}
          onError={() => {
            showToast('Error', 'Google login failed', "destructive");
          }}
          useOneTap={false}
          theme="outline"
          size="large"
          text={mode === 'login' ? 'signin_with' : 'signup_with'}
          shape="rectangular"
          width="100%"
        />
      </div>
    </>
  );


  const handleGoogleSuccess = async (credentialResponse) => {
    try {
      setIsLoading(true);

      const response = await fetch(`${API_URL}/auth/google`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token: credentialResponse.credential,
          mode: mode
        })
      });

      const data = await response.json();

      if (response.ok) {
        // Track GA4 event
        if (mode === 'login') {
          trackAuthEvents.loginSuccess('google');
        } else {
          trackAuthEvents.signupSuccess('google');
        }
        onLogin(data.user, data.token);
        navigate('/dashboard');
      } else if (response.status === 409) {
        showToast('Error', data.message, "destructive");
      } else {
        showToast('Error', data.message || 'Google authentication failed', "destructive");
      }
    } catch (error) {
      showToast('Error', handleError(error), "destructive");
    } finally {
      setIsLoading(false);
    }
  };

  const googleLogin = useGoogleLogin({
    onSuccess: handleGoogleSuccess,
    onError: () => {
      showToast('Error', 'Google login failed', "destructive");
    },
    flow: 'implicit' // Add this to get the ID token
  });

  const validatePassword = (password) => {
    const hasSpecialChar = specialChar.test(password);
    const isLongEnough = password.length >= 6;
    return {
      isValid: isLongEnough && hasSpecialChar,
      hasSpecialChar,
      isLongEnough
    };
  };

  const isFormValid = () => {
    if (mode === 'login') return true;
    const validation = validatePassword(password);
    return validation.isValid && password === confirmPassword;
  };

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const verificationToken = queryParams.get('token');
    const resetToken = queryParams.get('reset');

    if (verificationToken) {
      verifyEmail(verificationToken);
    }
    if (resetToken) {
      setMode('resetPassword');
      // Store the reset token for later use
      localStorage.setItem('resetToken', resetToken);
    }
  }, [location]);

  const handleError = (error) => {
    if (!navigator.onLine) {
      return 'No internet connection. Please check your connection and try again.';
    }
    if (error.message === 'Failed to fetch' || error.name === 'TypeError') {
      return 'Unable to reach the server. Please try again later.';
    }
    if (error instanceof SyntaxError && error.message.includes('JSON')) {
      return 'Unable to communicate with the server. Please try again later.';
    }
    return error.message || 'An unexpected error occurred. Please try again.';
  };

  const showToast = (title, description, variant = "default") => {
    toast({
      title,
      description,
      className: variant === "destructive" ? "bg-red-100 border-red-200 text-black" : "bg-black border-white text-white"
    });
  };

  const verifyEmail = async (token) => {
    setIsLoading(true);

    try {
      const response = await fetch(`${API_URL}/verify-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token })
      });

      const data = await response.json();

      if (response.ok) {
        trackAuthEvents.emailVerified();
        onLogin(data.user, data.token);
        navigate('/dashboard');
        showToast('Success', 'Email verified successfully!');
      } else {
        showToast('Error', data.message || 'Email verification failed', "destructive");
      }
    } catch (error) {
      showToast('Error', handleError(error), "destructive");
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await fetch(`${API_URL}/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (response.ok || response.status === 404) {
        showToast('Success', 'If an account exists with this email, you will receive password reset instructions.');
        setEmail('');
      } else {
        showToast('Error', data.message || 'Failed to process password reset request', "destructive");
      }
    } catch (error) {
      showToast('Error', handleError(error), "destructive");
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();

    const validation = validatePassword(password);
    if (!validation.isValid) {
      let errorMessage = 'Password requirements not met:';
      if (!validation.isLongEnough) {
        errorMessage += ' Must be at least 8 characters.';
      }
      if (!validation.hasSpecialChar) {
        errorMessage += ' Must contain a special character.';
      }
      showToast('Error', errorMessage, "destructive");
      return;
    }

    if (password !== confirmPassword) {
      showToast('Error', 'Passwords do not match', "destructive");
      return;
    }

    setIsLoading(true);
    try {
      const token = localStorage.getItem('resetToken');
      const response = await fetch(`${API_URL}/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password }),
      });
      const data = await response.json();

      if (response.ok) {
        trackAuthEvents.passwordReset();
        showToast('Success', 'Password reset successful! You can now login with your new password.');
        localStorage.removeItem('resetToken');
        setTimeout(() => setMode('login'), 2000);
      } else {
        showToast('Error', data.message || 'Failed to reset password', "destructive");
      }
    } catch (error) {
      showToast('Error', handleError(error), "destructive");
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendVerification = async () => {
    setIsLoading(true);

    try {
      const response = await fetch(`${API_URL}/resend-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (response.ok) {
        showToast('Success', 'Verification email resent! Please check your inbox.');
        setEmail('');
        setPassword('');
      } else {
        showToast('Error', data.message || 'Failed to resend verification email', "destructive");
      }
    } catch (error) {
      showToast('Error', handleError(error), "destructive");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (mode === 'register') {
      const validation = validatePassword(password);
      if (!validation.isValid) {
        let errorMessage = 'Password requirements not met:';
        if (!validation.isLongEnough) {
          errorMessage += ' Must be at least 8 characters.';
        }
        if (!validation.hasSpecialChar) {
          errorMessage += ' Must contain a special character.';
        }
        showToast('Error', errorMessage, "destructive");
        return;
      }

      if (password !== confirmPassword) {
        showToast('Error', 'Passwords do not match', "destructive");
        return;
      }
    }

    setIsLoading(true);

    try {
      const response = await fetch(`${API_URL}/${mode === 'login' ? 'login' : 'register'}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (response.ok) {
        if (mode === 'login') {
          trackAuthEvents.loginSuccess('email');
          setShowVerificationButton(false);
          onLogin(data.user, data.token);
          navigate('/dashboard');
        } else {
          trackAuthEvents.signupSuccess('email');
          showToast('Success', 'Registration successful! Please check your inbox to verify your account.');
          setEmail('');
          setPassword('');
        }
      } else if (response.status === 404 && mode === 'login') {
        showToast('Error', 'No account found with this email. Please register first.', "destructive");
        setShowVerificationButton(false);
      } else if (response.status === 403) {
        showToast('Error', 'Please verify your email before logging in!', "destructive");
        setShowVerificationButton(true);
      } else {
        showToast('Error', data.message || 'Authentication failed', "destructive");
        setShowVerificationButton(false);
      }
    } catch (error) {
      showToast('Error', handleError(error), "destructive");
      setShowVerificationButton(false);
    } finally {
      setIsLoading(false);
    }
  };

  const renderForm = () => {
    switch (mode) {
      case 'forgotPassword':
        return (
          <div className="space-y-6 w-full">
            <form onSubmit={handleForgotPassword} className="space-y-6">
              <Input
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full border border-none bg-yellow-100"
              />
              <Button type="submit" className="rounded-2xl bg-gray-900 text-white border-0 hover:bg-gray-600" disabled={isLoading}>
                {isLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : 'Send Reset Link'}
              </Button>
            </form>
          </div>
        );

      case 'resetPassword':
        return (
          <div className="space-y-6 w-full">
            <form onSubmit={handleResetPassword} className="space-y-6">
              <div className="relative w-full">
                <Input
                  type={showPassword ? "text" : "password"}
                  placeholder="New Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="w-full pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 flex items-center pr-3"
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4 text-gray-500" />
                  ) : (
                    <Eye className="h-4 w-4 text-gray-500" />
                  )}
                </button>
              </div>
              <div className="relative w-full">
                <>
                  <div className="relative w-full">
                    <Input
                      type={showConfirmPassword ? "text" : "password"}
                      placeholder="Confirm New Password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      className="w-full pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute inset-y-0 right-0 flex items-center pr-3"
                    >
                      {showConfirmPassword ? (
                        <EyeOff className="h-4 w-4 text-gray-500" />
                      ) : (
                        <Eye className="h-4 w-4 text-gray-500" />
                      )}
                    </button>

                  </div>
                  <div className="space-y-2 mt-4">
                    <div className="flex items-center space-x-2">
                      <ChevronRight
                        className={`h-4 w-4 ${password.length >= 6 ? 'text-green-500' : 'text-red-500'}`}
                      />
                      <p className={`text-sm ${password.length >= 6 ? 'text-green-500' : 'text-gray-500'}`}>
                        Minimum 6 characters
                      </p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <ChevronRight

                        className={`h-4 w-4 ${specialChar.test(password) ? 'text-green-500' : 'text-red-500'}`}
                      />
                      <p className={`text-sm ${specialChar.test(password) ? 'text-green-500' : 'text-gray-500'}`}>
                        Contains special character !@#§/=$%^&*(),.?&quot;:{ }|&lt;&gt;_~;°-
                      </p>
                    </div>
                    {password && confirmPassword && (
                      <div className="flex items-center space-x-2">
                        <ChevronRight
                          className={`h-4 w-4 ${password === confirmPassword ? 'text-green-500' : 'text-red-500'}`}
                        />
                        <p className={`text-sm ${password === confirmPassword ? 'text-green-500' : 'text-gray-500'}`}>
                          Passwords match
                        </p>
                      </div>
                    )}
                  </div>
                </>
              </div>
              <Button type="submit" disabled={isLoading || !isFormValid()} className="bg-gray-900 border-0 text-white hover:bg-gray-600 rounded-2xl"
              >
                {isLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : 'Reset Password'}
              </Button>
            </form>
          </div>
        );

      default:
        return (
          <div className="space-y-6 w-full">
            <form onSubmit={handleSubmit} className="space-y-6 w-full">
              <Input
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full border border-none bg-yellow-100"
              />
              <div className="relative w-full">
                <Input
                  type={showPassword ? "text" : "password"}
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="w-full border border-none bg-yellow-100"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 flex items-center pr-3"
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4 text-gray-500" />
                  ) : (
                    <Eye className="h-4 w-4 text-gray-500" />
                  )}
                </button>
              </div>
              {mode === 'register' && (
                <>
                  <div className="relative w-full">
                    <Input
                      type={showConfirmPassword ? "text" : "password"}
                      placeholder="Confirm Password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      className="w-full border border-none bg-yellow-100"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute inset-y-0 right-0 flex items-center pr-3"
                    >
                      {showConfirmPassword ? (
                        <EyeOff className="h-4 w-4 text-gray-500" />
                      ) : (
                        <Eye className="h-4 w-4 text-gray-500" />
                      )}
                    </button>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <ChevronRight
                        className={`h-4 w-4 ${password.length >= 6 ? 'text-green-500' : 'text-red-500'}`}
                      />
                      <p className={`text-sm ${password.length >= 6 ? 'text-green-500' : 'text-gray-500'}`}>
                        Minimum 6 characters
                      </p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <ChevronRight
                        className={`h-4 w-4 ${specialChar.test(password) ? 'text-green-500' : 'text-red-500'}`}
                      />
                      <p className={`text-sm ${specialChar.test(password) ? 'text-green-500' : 'text-gray-500'}`}>
                        Contains special character !@#§/=$%^&*(),.?&quot;:{ }|&lt;&gt;_~;°-
                      </p>
                    </div>
                    {password && confirmPassword && (
                      <div className="flex items-center space-x-2">
                        <ChevronRight
                          className={`h-4 w-4 ${password === confirmPassword ? 'text-green-500' : 'text-red-500'}`}
                        />
                        <p className={`text-sm ${password === confirmPassword ? 'text-green-500' : 'text-gray-500'}`}>
                          Passwords match
                        </p>
                      </div>
                    )}
                  </div>
                </>
              )}
              {showVerificationButton && mode === 'login' ? (
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleResendVerification}
                  disabled={isLoading}
                  className="rounded-2xl bg-gray-900 text-white border-0 hover:bg-gray-600"
                >
                  {isLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : 'Resend Verification Email'}
                </Button>
              ) : (
                <Button size="lg" type="submit" className="rounded-2xl bg-gray-900 text-white border-0 hover:bg-gray-600"
                  disabled={isLoading || !isFormValid()}>
                  {isLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {mode === 'login' ? 'Logging in...' : 'Registering...'}
                    </>
                  ) : (
                    mode === 'login' ? 'Login' : 'Register'
                  )}
                </Button>
              )}
            </form>
            {mode !== 'forgotPassword' && mode !== 'resetPassword' && renderGoogleButton()}
          </div>
        );
    }
  };

  return (
    <Card className="w-full max-w-md mx-auto shadow-xl border-none rounded-xl bg-gray-50">
      <CardHeader className="text-center text-2xl font-bold">
        {mode === 'login' ? 'Login' :
          mode === 'register' ? 'Register' :
            mode === 'forgotPassword' ? 'Reset Password' :
              'Set New Password'}
      </CardHeader>
      <CardContent>
        <div className="flex justify-center text-center">
          {renderForm()}
        </div>

        <div className="mt-4 text-center space-y-2">
          {mode === 'login' && (
            <>
              <Button variant="link" onClick={() => setMode('forgotPassword')}>
                Forgot Password?
              </Button>
              <p>
                Don't have an account?{' '}
                <Button variant="link" onClick={() => setMode('register')}>
                  Register
                </Button>
              </p>
            </>
          )}
          {mode === 'register' && (
            <p>
              Already have an account?{' '}
              <Button variant="link" onClick={() => setMode('login')}>
                Login
              </Button>
            </p>
          )}
          {(mode === 'forgotPassword' || mode === 'resetPassword') && (
            <Button variant="link" onClick={() => setMode('login')}>
              Back to Login
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default AuthComponent;